import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';
import { connect } from 'react-redux';
import { ApplicationState } from 'types';
import { User } from 'firebase/auth';
import { ThunkDispatch } from 'redux-thunk';
import { updateDhyanPassLock } from 'store/ux/actions';

interface PassLockDialogProps {
	open: boolean;
	mode: 'verify' | 'set';
	onClose: () => void;
	onSuccess: () => void;
	user?: User;
	currentPass?: string;
	updatePassLock: (passLock: string) => void;
}

const PassLockDialog = (props: PassLockDialogProps) => {
	const { open, mode, onClose, onSuccess, user, currentPass } = props;
	const [pass, setPass] = useState('');
	const [newPass, setNewPass] = useState('');
	const [confirmPass, setConfirmPass] = useState('');
	const [error, setError] = useState('');

	const handleClose = () => {
		setPass('');
		setNewPass('');
		setConfirmPass('');
		setError('');
		onClose();
	};

	const handleSubmit = () => {
		try {
			if (mode === 'verify') {
				if (pass === currentPass) {
					onSuccess();
					handleClose();
				} else {
					setError('Incorrect pass code');
				}
			} else {
				if (newPass !== confirmPass) {
					setError('New pass codes do not match');
					return;
				}

				if (newPass.length < 4) {
					setError('Pass code must be at least 4 characters');
					return;
				}

				props.updatePassLock(newPass);

				onSuccess();
				handleClose();
			}
		} catch (error) {
			setError('Failed to update pass code');
		}
	};

	return (
		<Dialog open={open} onClose={handleClose}>
			<DialogTitle>
				{mode === 'verify' && 'Enter Pass Code'}
				{mode === 'set' && 'Set Pass Code'}
			</DialogTitle>
			<DialogContent>
				{mode === 'verify' && (
					<TextField
						autoFocus
						margin="dense"
						label="Pass Code"
						type="text"
						sx={{ '& input': { '-webkit-text-security': 'disc' } }}
						fullWidth
						value={pass}
						onChange={(e) => setPass(e.target.value)}
						error={!!error}
						helperText={error}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								handleSubmit();
								e.preventDefault();
							}
						}}
					/>
				)}
				{mode === 'set' && (
					<>
						<TextField
							autoFocus={mode === 'set'}
							margin="dense"
							label="Pass Code"
							type="text"
							sx={{ '& input': { '-webkit-text-security': 'disc' } }}
							fullWidth
							value={newPass}
							onChange={(e) => setNewPass(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleSubmit();
									e.preventDefault();
								}
							}}
						/>
						<TextField
							margin="dense"
							label="Confirm Pass Code"
							type="text"
							sx={{ '& input': { '-webkit-text-security': 'disc' } }}
							fullWidth
							value={confirmPass}
							onChange={(e) => setConfirmPass(e.target.value)}
							error={!!error}
							helperText={error}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleSubmit();
									e.preventDefault();
								}
							}}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button onClick={handleSubmit} color="primary">
					{mode === 'verify' ? 'Verify' : 'Save'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

function mapStateToProps(state: ApplicationState) {
	return {
		user: state.userState.userStore.user,
		currentPass: state.uxState.dhyanPassLock,
	};
}

function mapDispatchToProps(dispatch: ThunkDispatch<any, any, any>) {
	return {
		updatePassLock: (passLock: string) => {
			dispatch(updateDhyanPassLock(passLock));
		},
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(PassLockDialog);
